import type {
  Study,
  AucResult,
  PkParameters,
  BioavailabilityResult,
  ComputationOutput,
  ValidationWarning,
} from '@bioavail/shared-types';
import {
  CalculationMode as CM,
  AucInterval as AI,
  RouteOfAdministration as ROA,
  BlqRule as BR,
} from '@bioavail/shared-types';
import { computeAucZeroToT } from './auc/trapezoidal';
import { estimateLambdaZ } from './auc/lambdaZ';
import { computePartialAuc } from './auc/partialAuc';
import {
  normalizeConcentration,
  normalizeTime,
  normalizeDose,
  checkUnitCompatibility,
  warn,
  WS,
  WC,
} from './units/normalize';
import { computeAbsoluteF, computeRelativeF } from './bioavailability/absoluteF';
import { computePkParameters } from './pkParams';
import { computeMrtMat } from './pkParams/mrtMat';
import { evaluateWarnings } from './validation/warnings';
import { buildFormula } from './bioavailability/absoluteF';
import { interpretationBand } from './formula/breakdown';

/**
 * Pure orchestrator (PSD §4 / P1.2.12).
 * normalize units → check compatibility → per-arm AUC (direct or from points) →
 * λ_z when AUC(0-∞) → absolute/relative F → PK params → warnings → formula.
 *
 * Returns a full ComputationOutput WITHOUT computedAt (caller injects).
 * Throws on block conditions so the store can surface lastError.
 */
export function computeStudy(study: Study): Omit<ComputationOutput, 'computedAt'> {
  // 1. Unit compatibility (block-level) — checked first.
  const unitWarnings = checkUnitCompatibility(study.arms);

  // 2. Per-arm AUC.
  const aucResults: AucResult[] = [];
  const selectedAucByArm = new Map<string, number>();

  for (const arm of study.arms) {
    const lloq = arm.lloq;

    if (arm.usesDirectAuc) {
      const directAuc = arm.directAuc ?? 0;
      if (directAuc <= 0) {
        throw new Error(`Arm "${arm.label}" has an invalid direct AUC (must be > 0).`);
      }
      // Normalize direct AUC to canonical ng/mL·h (concentration basis only;
      // direct AUC is already integrated over time).
      const normalizedAuc = normalizeConcentration(directAuc, arm.concentrationUnit);
      const auc: AucResult = {
        armId: arm.id,
        aucZeroToT: normalizedAuc,
        aucExtrapolated: 0,
        aucZeroToInf: normalizedAuc,
        extrapolationPercent: 0,
        lambdaZ: 0,
        lambdaZR2: 0,
        lambdaZPointsUsed: [],
        trapezoidalSegments: [],
        fromDirectAuc: true,
      };
      aucResults.push(auc);
      selectedAucByArm.set(arm.id, normalizedAuc);
      continue;
    }

    // Normalize points to canonical units.
    const normalizedPoints = arm.dataPoints.map((p) => ({
      time: normalizeTime(p.time, arm.timeUnit),
      concentration: normalizeConcentration(p.concentration, arm.concentrationUnit),
      isBlq: p.isBlq,
    }));

    // 2a. AUC(0-t).
    const trap = computeAucZeroToT(normalizedPoints, arm.trapezoidalRule, arm.blqRule, lloq);
    const aucZeroToT = trap.aucZeroToT;
    let aucExtrapolated = 0;
    let lambdaZ = 0;
    let lambdaZR2 = 0;
    let lambdaZPointsUsed: number[] = [];

    // 2b. λ_z & AUC(0-∞) when requested.
    if (study.aucInterval === AI.ZeroToInf) {
      const lz = estimateLambdaZ(
        normalizedPoints,
        aucZeroToT,
        arm.lambdaZManualOverride ? arm.lambdaZStartIndex : undefined,
        arm.lambdaZManualOverride ? arm.lambdaZEndIndex : undefined,
      );
      lambdaZ = lz.lambdaZ;
      lambdaZR2 = lz.r2;
      lambdaZPointsUsed = lz.pointsUsed;
      aucExtrapolated = lz.aucExtrapolated;
    }

    const aucZeroToInf = aucZeroToT + aucExtrapolated;

    // 2c. Partial AUC (Phase 2).
    let aucPartial: number | undefined;
    if (study.aucInterval === AI.Interval && study.partialAucWindow) {
      const partial = computePartialAuc(
        normalizedPoints,
        study.partialAucWindow,
        arm.trapezoidalRule,
        arm.blqRule,
        lloq,
      );
      aucPartial = partial.aucPartial;
    }

    const extrapPercent = aucZeroToInf > 0 ? (aucExtrapolated / aucZeroToInf) * 100 : 0;

    const auc: AucResult = {
      armId: arm.id,
      aucZeroToT,
      aucExtrapolated,
      aucZeroToInf,
      ...(aucPartial !== undefined ? { aucPartial } : {}),
      extrapolationPercent: extrapPercent,
      lambdaZ,
      lambdaZR2,
      lambdaZPointsUsed,
      trapezoidalSegments: trap.segments,
      fromDirectAuc: false,
    };
    aucResults.push(auc);

    // Selected AUC value used for F.
    const selected =
      study.aucInterval === AI.Interval
        ? aucPartial ?? aucZeroToT
        : study.aucInterval === AI.ZeroToInf
          ? aucZeroToInf
          : aucZeroToT;
    selectedAucByArm.set(arm.id, selected ?? aucZeroToT);
  }

  // 3. Determine test / reference arms.
  const testArm = study.arms.find((a) => a.role === 'test') ?? study.arms[0];
  const refArm = study.arms.find((a) => a.role === 'reference') ?? study.arms[1];
  if (!testArm || !refArm) {
    throw new Error('A study requires exactly two arms (test and reference).');
  }

  const selectedAucTest = selectedAucByArm.get(testArm.id) ?? 0;
  const selectedAucReference = selectedAucByArm.get(refArm.id) ?? 0;

  const doseTest = normalizeDose(testArm.dose, testArm.doseUnit);
  const doseReference = normalizeDose(refArm.dose, refArm.doseUnit);

  // 4. F computation.
  const fInput = { aucTest: selectedAucTest, aucRef: selectedAucReference, doseTest, doseRef: doseReference };
  const fResult =
    study.mode === CM.Absolute ? computeAbsoluteF(fInput) : computeRelativeF(fInput);

  const formula = buildFormula(
    study.mode,
    selectedAucTest,
    selectedAucReference,
    doseTest,
    doseReference,
    fResult,
  );
  const interpretation = interpretationBand(fResult.fPercent, study.mode);

  const bioavailability: BioavailabilityResult = {
    mode: study.mode,
    fRaw: fResult.fRaw,
    fDoseCorrected: fResult.fDoseCorrected,
    fPercent: fResult.fPercent,
    doseNormalized: fResult.doseNormalized,
    formula,
    interpretation,
    aucBasis: study.aucInterval,
    selectedAucTest,
    selectedAucReference,
    doseTest,
    doseReference,
  };

  // 5. PK parameters.
  const pkParameters: PkParameters[] = study.arms.map((arm) => {
    const auc = aucResults.find((a) => a.armId === arm.id)!;
    const normalizedDose = normalizeDose(arm.dose, arm.doseUnit);
    const pk = computePkParameters(arm, auc, normalizedDose);
    // MRT/MAT (Phase 2).
    if (study.arms.some((a) => a.route === ROA.IV)) {
      const { mrt, mat } = computeMrtMat(study.arms, aucResults);
      const armMrt = mrt.find((m) => m.armId === arm.id);
      return {
        ...pk,
        ...(armMrt ? { mrt: armMrt.value } : {}),
        ...(mat !== null ? { mat } : {}),
      };
    }
    return pk;
  });

  // 5a. Dose-normalized Cmax test/reference ratio (F-068).
  if (!testArm.usesDirectAuc && !refArm.usesDirectAuc) {
    const pkTest = pkParameters.find((p) => p.armId === testArm.id);
    const pkRef = pkParameters.find((p) => p.armId === refArm.id);
    const cmaxTest = pkTest?.cmax ?? 0;
    const cmaxRef = pkRef?.cmax ?? 0;
    if (cmaxRef > 0 && doseTest > 0) {
      const ratio = (cmaxTest * doseReference) / (cmaxRef * doseTest);
      if (Number.isFinite(ratio)) {
        bioavailability.cmaxDoseNormalizedRatio = { ratio, percent: ratio * 100 };
      }
    }
  }

  // 6. Warnings.
  let warnings: ValidationWarning[] = [...unitWarnings];
  warnings = warnings.concat(evaluateWarnings(study, aucResults, bioavailability, pkParameters));

  return {
    bioavailability,
    aucResults,
    pkParameters,
    warnings,
  };
}

export { warn, WS, WC, BR };
